<template>
  <a-modal
    :open="open"
    :width="980"
    :footer="null"
    @cancel="handleClose"
  >
    <a-spin :spinning="loading">
      <div class="detail-modal-body">
        <!-- 左侧：安全员信息 -->
        <div class="detail-left">
          <!-- 基本信息 -->
          <div class="form-section">
            <div class="section-title">基本信息</div>
            <a-row :gutter="24">
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">姓名</span>
                  <span class="detail-value">{{ detail.name || "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">手机号码</span>
                  <span class="detail-value">{{ detail.phone || "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">身份证号</span>
                  <span class="detail-value">{{ detail.idCard || "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">性别</span>
                  <span class="detail-value">{{ genderLabel }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">培训日期</span>
                  <span class="detail-value">{{ detail.trainingDate || "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">状态</span>
                  <span
                    class="detail-value"
                    :class="detail.status === 1 ? 'status-normal' : 'status-disabled'"
                  >
                    {{ detail.status === 1 ? "正常" : "停用" }}
                  </span>
                </div>
              </a-col>
            </a-row>
          </div>

          <!-- 证件照片 -->
          <div class="form-section">
            <div class="section-title">证件照片</div>
            <a-row :gutter="24">
              <a-col :span="12">
                <div class="photo-col">
                  <span class="photo-label">身份证正面</span>
                  <ImageUploader
                    v-if="detail.idPhotoFrontUrl"
                    v-model:imageUrl="detail.idPhotoFrontUrl"
                    only-preview
                    :box-width="240"
                    :box-height="150"
                  />
                  <span v-else class="empty-tip">未上传</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="photo-col">
                  <span class="photo-label">身份证反面</span>
                  <ImageUploader
                    v-if="detail.idPhotoBackUrl"
                    v-model:imageUrl="detail.idPhotoBackUrl"
                    only-preview
                    :box-width="240"
                    :box-height="150"
                  />
                  <span v-else class="empty-tip">未上传</span>
                </div>
              </a-col>
            </a-row>
          </div>

          <!-- 培训证明附件 -->
          <div class="form-section">
            <div class="section-title">培训证明附件</div>
            <ul v-if="certFiles.length" class="cert-file-list">
              <li v-for="file in certFiles" :key="file.fileUrl" class="cert-file-item">
                <FileOutlined class="file-icon" />
                <a class="file-name" @click="handlePreviewFile(file)">{{ file.fileName }}</a>
              </li>
            </ul>
            <div v-else class="empty-tip">暂无培训证明附件</div>
          </div>
        </div>

        <!-- 右侧：审核历史信息（暂未设计） -->
        <div class="detail-right">
          <div class="form-section">
            <div class="section-title">审核历史</div>
            <a-empty description="暂无审核记录" />
          </div>
        </div>
      </div>
    </a-spin>

    <!-- 培训证明文件预览抽屉 -->
    <FilePreview
      v-model:visible="previewVisible"
      :file-url="previewFileUrl"
      :file-name="previewFileName"
      @close="previewVisible = false"
    />
  </a-modal>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import { message } from "ant-design-vue";
import { FileOutlined } from "@ant-design/icons-vue";
import ImageUploader from "@/components/imageUploader/index.vue";
import FilePreview from "@/components/FilePreview/index.vue";
import { getSecurityUserDetail } from "@/api/securityUser/securityUser";

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  securityUserId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(["update:open", "close"]);

const loading = ref(false);

// 安全员详情
const detail = reactive({
  name: "",
  phone: "",
  idCard: "",
  gender: undefined,
  trainingDate: undefined,
  status: 1,
  idPhotoFrontUrl: "",
  idPhotoBackUrl: "",
});

// 培训证明附件列表
const certFiles = ref([]);

// 文件预览
const previewVisible = ref(false);
const previewFileUrl = ref("");
const previewFileName = ref("");

const genderLabel = computed(() => {
  if (detail.gender === 1) return "男";
  if (detail.gender === 0) return "女";
  return "-";
});

// 加载安全员详情
const loadDetail = async () => {
  if (!props.securityUserId) return;
  loading.value = true;
  try {
    const res = await getSecurityUserDetail(props.securityUserId);
    if (res.code === 200 && res.data) {
      const d = res.data;
      detail.name = d.name || "";
      detail.phone = d.phone || "";
      detail.idCard = d.idCard || "";
      detail.gender = d.gender;
      detail.trainingDate = d.trainingDate || undefined;
      detail.status = d.status ?? 1;
      detail.idPhotoFrontUrl = d.idPhotoFrontUrl || "";
      detail.idPhotoBackUrl = d.idPhotoBackUrl || "";
      certFiles.value = Array.isArray(d.trainingCertFiles) ? d.trainingCertFiles : [];
    } else {
      message.error(res.message || "获取详情失败");
    }
  } catch (error) {
    console.error("获取安全员详情失败:", error);
    message.error("获取安全员详情失败");
  } finally {
    loading.value = false;
  }
};

// 弹框打开时加载详情
watch(
  () => props.open,
  (val) => {
    if (val) {
      loadDetail();
    }
  }
);

// 点击培训证明文件预览
const handlePreviewFile = (file) => {
  previewFileUrl.value = file.fileUrl;
  previewFileName.value = file.fileName || "文件预览";
  previewVisible.value = true;
};

const handleClose = () => {
  emit("update:open", false);
  emit("close");
};
</script>

<style scoped lang="less">
.detail-modal-body {
  display: flex;
  gap: 24px;
  min-height: 320px;
}

.detail-left {
  flex: 1;
  min-width: 0;
}

.detail-right {
  width: 300px;
  flex-shrink: 0;
  border-left: 1px solid #f0f0f0;
  padding-left: 24px;
}

.form-section {
  margin-bottom: 24px;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 16px;
    padding-left: 10px;
    border-left: 3px solid #1890ff;
    line-height: 1;
  }
}

.detail-item {
  display: flex;
  margin-bottom: 12px;

  .detail-label {
    width: 80px;
    flex-shrink: 0;
    color: #8c8c8c;
  }

  .detail-value {
    color: #262626;
    word-break: break-all;
  }
}

.status-normal {
  color: #52c41a;
}

.photo-col {
  .photo-label {
    display: block;
    margin-bottom: 8px;
    color: #8c8c8c;
  }
}

.status-disabled {
  color: #999;
}

.cert-file-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.cert-file-item {
  display: flex;
  align-items: center;
  padding: 6px 0;

  .file-icon {
    color: #1890ff;
    margin-right: 8px;
  }

  .file-name {
    color: #1e8ef2;
  }
}

.empty-tip {
  color: #999;
  font-size: 13px;
}
</style>
